import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";
import { useBank } from "./BankContext";
import ReadinessCard from "./ReadinessCard";

const { Column } = Table;
const { Text } = Typography;

/** Effective per-level values from the formula (mirrors setu
 * app/helper/quiz.level_config — keep in sync). */
const formulaLevel = (progression, level) => {
  const p = progression || {};
  return {
    questions_count: (p.base_questions ?? 20) + (p.questions_step ?? 5) * (level - 1),
    coins_per_correct: (p.base_coins ?? 5) + (p.coins_step ?? 1) * (level - 1),
    pass_pct: Math.min(
      p.pass_cap ?? 90,
      (p.base_pass_pct ?? 60) + (p.pass_step ?? 5) * (level - 1)
    ),
  };
};

const ConfigTab = () => {
  const { token, apiUrl } = useContext(Context);
  const { configKey } = useBank();

  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/?configKey=${configKey}`, token, (result) => {
      if (result && result.config) setConfig(result.config);
    });
  }, [apiUrl, token, configKey]);

  if (!config) return null;

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const setProg = (patch) =>
    setConfig((c) => ({ ...c, progression: { ...(c.progression || {}), ...patch } }));
  const setAdaptive = (patch) =>
    setConfig((c) => ({ ...c, adaptive: { ...(c.adaptive || {}), ...patch } }));

  const overrides = config.level_overrides || [];

  const updateOverride = (index, patch) =>
    set({
      level_overrides: overrides.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    });

  const addOverride = () => {
    const used = overrides.map((o) => o.level);
    let level = 1;
    while (used.includes(level)) level += 1;
    set({
      level_overrides: [
        ...overrides,
        { level, questions_count: null, coins_per_correct: null, pass_pct: null, active: true },
      ],
    });
  };

  const saveError = (result) => {
    const detail = result && result.detail;
    let message = "Save failed";
    if (typeof detail === "string" && detail) {
      message = detail;
    } else if (Array.isArray(detail) && detail[0]) {
      const first = detail[0];
      const where = Array.isArray(first.loc)
        ? first.loc.filter((part) => part !== "body").join(".")
        : "";
      message = where ? `${where}: ${first.msg}` : first.msg || message;
    }
    notification.error({ message });
  };

  const save = (extra) => {
    const c = { ...config, ...(extra || {}) };
    if (configKey === "reasoning") {
      const missing = (c.categories || []).find(
        (row) =>
          !(row.id || "").trim() ||
          !(row.title || "").trim() ||
          !(row.topic || "").trim()
      );
      if (missing) {
        notification.error({
          message: "Each category needs an id, a title, and a topic. The title is the name in the app. The topic is what new questions are written about.",
        });
        return;
      }
    }
    const body = {
      status: c.status,
      max_level: c.max_level,
      hearts_free_per_level: c.hearts_free_per_level,
      hearts_cap: c.hearts_cap,
      hearts_max_per_question: c.hearts_max_per_question,
      ad_gate_every: c.ad_gate_every,
      session_ttl_minutes: c.session_ttl_minutes,
      min_answer_interval_ms: c.min_answer_interval_ms,
      lifeline_earn_min_interval_s: c.lifeline_earn_min_interval_s,
      daily_coin_cap: c.daily_coin_cap || 0,
      daily_attempts_per_level: c.daily_attempts_per_level ?? 3,
      min_seconds_per_question: c.min_seconds_per_question ?? 4,
      min_seconds_per_ad: c.min_seconds_per_ad ?? 15,
      level_unlock_ad_required: !!c.level_unlock_ad_required,
      level_unlock_min_app_version: c.level_unlock_min_app_version ?? 0,
      level_unlock_min_interval_s: c.level_unlock_min_interval_s ?? 15,
      level_unlock_fail_max_attempts: c.level_unlock_fail_max_attempts ?? 0,
      progression: c.progression,
      ...(c.adaptive ? { adaptive: c.adaptive } : {}),
      level_overrides: (c.level_overrides || []).map((o) => ({
        level: o.level,
        questions_count: o.questions_count ?? null,
        coins_per_correct: o.coins_per_correct ?? null,
        pass_pct: o.pass_pct ?? null,
        topic_tags: o.topic_tags ?? null,
        active: o.active !== false,
      })),
      info_points: c.info_points || [],
      info_points_hi: c.info_points_hi || [],
      ...(configKey === "reasoning"
        ? {
            categories: (c.categories || []).map((row) => ({
              id: (row.id || "").trim(),
              title: (row.title || "").trim(),
              description: (row.description || "").trim(),
              image: (row.image || "").trim() || null,
              section: row.section === "earn" ? "earn" : "quiz",
              display_order: Number(row.display_order) || 0,
              enabled: row.enabled !== false,
              topic: (row.topic || "").trim(),
              questions_count: row.questions_count || null,
              levels: (row.levels || []).map((lv, index) => ({
                id: (lv.id || "").trim(),
                title: (lv.title || "").trim(),
                display_order: Number(lv.display_order) || index,
                level: Number(lv.level) || 1,
                questions_count: lv.questions_count || null,
                enabled: lv.enabled !== false,
              })),
            })),
          }
        : {}),
    };
    setSaving(true);
    Put(
      `${apiUrl}/v4/quiz-config/?configKey=${configKey}`,
      token,
      body,
      (result) => {
        setSaving(false);
        if (result && result.config) {
          setConfig(result.config);
          notification.success({ message: "Quiz config saved" });
        } else {
          saveError(result);
        }
      },
      () => {
        setSaving(false);
        notification.error({ message: "Save failed" });
      }
    );
  };

  const active = config.status === "active";
  const prog = config.progression || {};

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Alert
        type={active ? "success" : "warning"}
        showIcon
        message={
          active
            ? `Quiz LIVE (version ${config.version || 0}).`
            : "Quiz paused — users see nothing until activated."
        }
        description="Activation requires enough APPROVED level-1 questions for its question count. In-flight sessions keep the config they started with."
        action={
          <Switch
            checked={active}
            checkedChildren="Active"
            unCheckedChildren="Paused"
            loading={saving}
            onChange={(checked) => save({ status: checked ? "active" : "paused" })}
          />
        }
      />

      <ReadinessCard />

      <Card title="Progression formula (levels without an override)" size="small">
        <Space size="large" wrap>
          <Space direction="vertical" size={2}>
            <Text>Level 1 questions</Text>
            <InputNumber min={1} max={100} value={prog.base_questions}
              onChange={(v) => setProg({ base_questions: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>+ questions / level</Text>
            <InputNumber min={0} max={50} value={prog.questions_step}
              onChange={(v) => setProg({ questions_step: v || 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Level 1 coins / correct</Text>
            <InputNumber min={0} value={prog.base_coins}
              onChange={(v) => setProg({ base_coins: v || 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>+ coins / level</Text>
            <InputNumber min={0} value={prog.coins_step}
              onChange={(v) => setProg({ coins_step: v || 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Level 1 pass %</Text>
            <InputNumber min={1} max={100} value={prog.base_pass_pct}
              onChange={(v) => setProg({ base_pass_pct: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>+ pass % / level</Text>
            <InputNumber min={0} max={50} value={prog.pass_step}
              onChange={(v) => setProg({ pass_step: v || 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Pass % cap</Text>
            <InputNumber min={1} max={100} value={prog.pass_cap}
              onChange={(v) => setProg({ pass_cap: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Max level</Text>
            <InputNumber min={1} max={500} value={config.max_level}
              onChange={(v) => set({ max_level: v || 1 })} />
          </Space>
        </Space>
        <Divider style={{ margin: "12px 0" }} />
        <Text type="secondary">
          Preview: L1 = {formulaLevel(prog, 1).questions_count}q /{" "}
          {formulaLevel(prog, 1).coins_per_correct}c / {formulaLevel(prog, 1).pass_pct}% ·
          L5 = {formulaLevel(prog, 5).questions_count}q /{" "}
          {formulaLevel(prog, 5).coins_per_correct}c / {formulaLevel(prog, 5).pass_pct}% ·
          L10 = {formulaLevel(prog, 10).questions_count}q /{" "}
          {formulaLevel(prog, 10).coins_per_correct}c / {formulaLevel(prog, 10).pass_pct}%
        </Text>
      </Card>

      <Card
        title="Adaptive difficulty (per-player question grade + pass cutoff — coins are NEVER adaptive)"
        size="small"
        extra={
          <Switch
            checked={!!(config.adaptive || {}).enabled}
            checkedChildren="On"
            unCheckedChildren="Off"
            onChange={(v) => setAdaptive({ enabled: v })}
          />
        }
      >
        <Text type="secondary">
          Once per day, each player's yesterday moves their difficulty step
          (1-5) and pass-mark delta: comfortable clear = harder + higher bar,
          failed day = easier + lower bar. Switch on only after every grade
          shows healthy in the pool matrix below. Save to apply.
        </Text>
        <Divider style={{ margin: "12px 0" }} />
        <Space size="large" wrap>
          <Space direction="vertical" size={2}>
            <Text>Pass step % / day</Text>
            <InputNumber min={0} max={50} value={(config.adaptive || {}).pass_step_pct ?? 5}
              onChange={(v) => setAdaptive({ pass_step_pct: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Pass floor %</Text>
            <InputNumber min={1} max={100} value={(config.adaptive || {}).pass_floor_pct ?? 50}
              onChange={(v) => setAdaptive({ pass_floor_pct: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Pass ceiling %</Text>
            <InputNumber min={1} max={100} value={(config.adaptive || {}).pass_ceiling_pct ?? 95}
              onChange={(v) => setAdaptive({ pass_ceiling_pct: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Delta min (pts)</Text>
            <InputNumber min={-50} max={0} value={(config.adaptive || {}).pass_delta_min ?? -10}
              onChange={(v) => setAdaptive({ pass_delta_min: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Delta max (pts)</Text>
            <InputNumber min={0} max={50} value={(config.adaptive || {}).pass_delta_max ?? 35}
              onChange={(v) => setAdaptive({ pass_delta_max: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Comfort margin (pts over pass)</Text>
            <InputNumber min={0} max={50} value={(config.adaptive || {}).comfort_margin_pct ?? 15}
              onChange={(v) => setAdaptive({ comfort_margin_pct: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Levels per grade (intra-day ramp)</Text>
            <InputNumber min={1} max={50} value={(config.adaptive || {}).ramp_levels_per_grade ?? 5}
              onChange={(v) => setAdaptive({ ramp_levels_per_grade: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Step floor 2 at level ≥</Text>
            <InputNumber min={1} value={(config.adaptive || {}).band_low_level ?? 5}
              onChange={(v) => setAdaptive({ band_low_level: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Step floor 3 at level ≥</Text>
            <InputNumber min={1} value={(config.adaptive || {}).band_mid_level ?? 10}
              onChange={(v) => setAdaptive({ band_mid_level: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Step floor 5 at level ≥</Text>
            <InputNumber min={1} value={(config.adaptive || {}).band_high_level ?? 15}
              onChange={(v) => setAdaptive({ band_high_level: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Idle decay after (days)</Text>
            <InputNumber min={1} max={60} value={(config.adaptive || {}).idle_decay_days ?? 7}
              onChange={(v) => setAdaptive({ idle_decay_days: v || 1 })} />
          </Space>
        </Space>
      </Card>

      <Card
        title="Per-level overrides (blank field = formula value)"
        size="small"
      >
        <Table
          dataSource={overrides.map((o, i) => ({ ...o, key: i }))}
          pagination={false}
          size="small"
        >
          <Column
            title="Level"
            width={90}
            render={(_, r, i) => (
              <InputNumber min={1} value={r.level}
                onChange={(v) => updateOverride(i, { level: v || 1 })} />
            )}
          />
          <Column
            title="Questions"
            render={(_, r, i) => (
              <InputNumber
                min={1}
                max={1000}
                placeholder={String(formulaLevel(prog, r.level).questions_count)}
                value={r.questions_count}
                onChange={(v) => updateOverride(i, { questions_count: v ?? null })}
              />
            )}
          />
          <Column
            title="Coins / correct"
            render={(_, r, i) => (
              <InputNumber
                min={0}
                placeholder={String(formulaLevel(prog, r.level).coins_per_correct)}
                value={r.coins_per_correct}
                onChange={(v) => updateOverride(i, { coins_per_correct: v ?? null })}
              />
            )}
          />
          <Column
            title="Pass %"
            render={(_, r, i) => (
              <InputNumber
                min={1}
                max={100}
                placeholder={String(formulaLevel(prog, r.level).pass_pct)}
                value={r.pass_pct}
                onChange={(v) => updateOverride(i, { pass_pct: v ?? null })}
              />
            )}
          />
          <Column
            title="Max coins"
            render={(_, r) => {
              const eff = formulaLevel(prog, r.level);
              const q = r.questions_count ?? eff.questions_count;
              const c = r.coins_per_correct ?? eff.coins_per_correct;
              return <Tag color="gold">{q * c} coins</Tag>;
            }}
          />
          <Column
            title="Active"
            width={80}
            render={(_, r, i) => (
              <Switch
                checked={r.active !== false}
                onChange={(v) => updateOverride(i, { active: v })}
              />
            )}
          />
          <Column
            width={60}
            render={(_, r, i) => (
              <Popconfirm
                title="Remove override?"
                onConfirm={() =>
                  set({ level_overrides: overrides.filter((_, j) => j !== i) })
                }
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          />
        </Table>
        <Button style={{ marginTop: 12 }} icon={<PlusOutlined />} onClick={addOverride}>
          Add override
        </Button>
      </Card>

      <Card title="Hearts, ads & limits" size="small">
        <Space size="large" wrap>
          <Space direction="vertical" size={2}>
            <Text>Free hearts / level</Text>
            <InputNumber min={0} max={10} value={config.hearts_free_per_level}
              onChange={(v) => set({ hearts_free_per_level: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Hearts cap (held)</Text>
            <InputNumber min={0} max={10} value={config.hearts_cap}
              onChange={(v) => set({ hearts_cap: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Max hearts / question</Text>
            <InputNumber min={1} max={2} value={config.hearts_max_per_question}
              onChange={(v) => set({ hearts_max_per_question: v || 1 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Rewarded-ad gate every N questions (0 = off)</Text>
            <InputNumber min={0} max={50} value={config.ad_gate_every}
              onChange={(v) => set({ ad_gate_every: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Session TTL (minutes)</Text>
            <InputNumber min={5} value={config.session_ttl_minutes}
              onChange={(v) => set({ session_ttl_minutes: v || 5 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Min answer interval (ms)</Text>
            <InputNumber min={0} value={config.min_answer_interval_ms}
              onChange={(v) => set({ min_answer_interval_ms: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Heart-earn cooldown (s)</Text>
            <InputNumber min={0} value={config.lifeline_earn_min_interval_s}
              onChange={(v) => set({ lifeline_earn_min_interval_s: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Daily coin cap (0 = uncapped here)</Text>
            <InputNumber min={0} value={config.daily_coin_cap}
              onChange={(v) => set({ daily_coin_cap: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Daily attempts per level (0 = unlimited)</Text>
            <InputNumber min={0} value={config.daily_attempts_per_level ?? 3}
              onChange={(v) => set({ daily_attempts_per_level: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Min seconds per question (0 = off)</Text>
            <InputNumber min={0} value={config.min_seconds_per_question ?? 4}
              onChange={(v) => set({ min_seconds_per_question: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Min seconds per rewarded ad (0 = off)</Text>
            <InputNumber min={0} value={config.min_seconds_per_ad ?? 15}
              onChange={(v) => set({ min_seconds_per_ad: v ?? 0 })} />
          </Space>
        </Space>
      </Card>

      <Card
        title="Next-level lock (rewarded ad to unlock)"
        size="small"
        extra={
          <Switch
            checked={!!config.level_unlock_ad_required}
            checkedChildren="On"
            unCheckedChildren="Off"
            onChange={(v) => set({ level_unlock_ad_required: v })}
          />
        }
      >
        <Text type="secondary">
          When on, after passing a level the next level stays locked until the
          player watches a rewarded ad. Only applies to app builds at or above
          the min app version below — older builds keep playing without the
          lock. Players are never locked out of the level they are already on.
          Save to apply.
        </Text>
        <Divider style={{ margin: "12px 0" }} />
        <Space size="large" wrap>
          <Space direction="vertical" size={2}>
            <Text>Min app version code (0 = all builds)</Text>
            <InputNumber min={0} value={config.level_unlock_min_app_version ?? 0}
              onChange={(v) => set({ level_unlock_min_app_version: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Unlock cooldown (s, 0 = off)</Text>
            <InputNumber min={0} value={config.level_unlock_min_interval_s ?? 15}
              onChange={(v) => set({ level_unlock_min_interval_s: v ?? 0 })} />
          </Space>
          <Space direction="vertical" size={2}>
            <Text>Failed ad attempts before 1 coin (0 = off)</Text>
            <InputNumber min={0} value={config.level_unlock_fail_max_attempts ?? 0}
              onChange={(v) => set({ level_unlock_fail_max_attempts: v ?? 0 })} />
          </Space>
        </Space>
        <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          If the unlock ad fails this many times (no fill / load or
          show error) in one app session, the player gets 1 coin. Once per
          locked level per day; the count restarts when the app is reopened.
        </Text>
      </Card>

      <Card title="Info points (app 'how it works')" size="small">
        <Text type="secondary">One point per line.</Text>
        <Divider style={{ margin: "8px 0" }} />
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input.TextArea
            rows={3}
            placeholder="English"
            value={(config.info_points || []).join("\n")}
            onChange={(e) => set({ info_points: e.target.value.split("\n") })}
          />
          <Input.TextArea
            rows={3}
            placeholder="Hindi"
            value={(config.info_points_hi || []).join("\n")}
            onChange={(e) => set({ info_points_hi: e.target.value.split("\n") })}
          />
        </Space>
      </Card>

      {configKey === "reasoning" && (
        <Card
          title="Gyanni categories"
          size="small"
          extra={
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                const rows = config.categories || [];
                set({
                  categories: [
                    ...rows,
                    {
                      id: "",
                      title: "",
                      description: "",
                      image: "",
                      section: "quiz",
                      display_order: rows.length,
                      enabled: true,
                      topic: "",
                      questions_count: null,
                      levels: [
                        {
                          id: "1",
                          title: "Level 1",
                          level: 1,
                          display_order: 0,
                          enabled: true,
                          questions_count: null,
                        },
                      ],
                    },
                  ],
                });
              }}
            >
              Add category
            </Button>
          }
        >
          <Text type="secondary">
            Quiz and Earn each show only the categories assigned to them.
            The id is what the app sends. Topic must match the topic on
            approved questions. Leave question count empty to use the level formula.
          </Text>
          <Table
            style={{ marginTop: 12 }}
            size="small"
            pagination={false}
            dataSource={(config.categories || []).map((row, index) => ({ ...row, _key: index }))}
            rowKey="_key"
            scroll={{ x: 960 }}
          >
            <Column
              title="Id"
              render={(_, row, index) => (
                <Input
                  value={row.id}
                  placeholder="analogies"
                  onChange={(e) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, id: e.target.value };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="Title"
              render={(_, row, index) => (
                <Input
                  value={row.title}
                  onChange={(e) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, title: e.target.value };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="Section"
              width={120}
              render={(_, row, index) => (
                <Select
                  value={row.section === "earn" ? "earn" : "quiz"}
                  options={[
                    { value: "quiz", label: "Quiz" },
                    { value: "earn", label: "Earn" },
                  ]}
                  onChange={(value) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, section: value };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="Topic"
              render={(_, row, index) => (
                <Input
                  value={row.topic}
                  placeholder="question topic"
                  onChange={(e) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, topic: e.target.value };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="Order"
              width={90}
              render={(_, row, index) => (
                <InputNumber
                  min={0}
                  max={10000}
                  value={row.display_order ?? 0}
                  onChange={(value) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, display_order: value || 0 };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="Questions"
              width={110}
              render={(_, row, index) => (
                <InputNumber
                  min={1}
                  max={100}
                  value={row.questions_count}
                  placeholder="level"
                  onChange={(value) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, questions_count: value || null };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              title="On"
              width={70}
              render={(_, row, index) => (
                <Switch
                  checked={row.enabled !== false}
                  onChange={(checked) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, enabled: checked };
                    set({ categories });
                  }}
                />
              )}
            />
            <Column
              width={48}
              render={(_, __, index) => (
                <Popconfirm
                  title="Remove this category?"
                  onConfirm={() => {
                    const categories = [...(config.categories || [])];
                    categories.splice(index, 1);
                    set({ categories });
                  }}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            />
          </Table>
          <Space direction="vertical" style={{ width: "100%", marginTop: 12 }}>
            {(config.categories || []).map((row, index) => (
              <Space key={`desc-${index}`} direction="vertical" size={2} style={{ width: "100%" }}>
                <Text type="secondary">{row.title || row.id || `Category ${index + 1}`}</Text>
                <Input
                  placeholder="Short description"
                  value={row.description}
                  onChange={(e) => {
                    const categories = [...(config.categories || [])];
                    categories[index] = { ...row, description: e.target.value };
                    set({ categories });
                  }}
                />
                <Space wrap>
                  {(row.levels || []).map((lv, levelIndex) => (
                    <Space key={`${row.id || index}-lv-${levelIndex}`}>
                      <Input
                        style={{ width: 120 }}
                        placeholder="Level id"
                        value={lv.id}
                        onChange={(e) => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels[levelIndex] = { ...lv, id: e.target.value };
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                      <Input
                        style={{ width: 140 }}
                        placeholder="Level name"
                        value={lv.title}
                        onChange={(e) => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels[levelIndex] = { ...lv, title: e.target.value };
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                      <InputNumber
                        min={1}
                        max={500}
                        value={lv.level}
                        onChange={(value) => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels[levelIndex] = { ...lv, level: value || 1 };
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                      <InputNumber
                        min={1}
                        max={100}
                        placeholder="Questions"
                        value={lv.questions_count}
                        onChange={(value) => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels[levelIndex] = { ...lv, questions_count: value || null };
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                      <Switch
                        checked={lv.enabled !== false}
                        onChange={(checked) => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels[levelIndex] = { ...lv, enabled: checked };
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const categories = [...(config.categories || [])];
                          const levels = [...(row.levels || [])];
                          levels.splice(levelIndex, 1);
                          categories[index] = { ...row, levels };
                          set({ categories });
                        }}
                      />
                    </Space>
                  ))}
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const categories = [...(config.categories || [])];
                      const levels = [...(row.levels || [])];
                      const next = levels.length + 1;
                      levels.push({
                        id: String(next),
                        title: `Level ${next}`,
                        level: next,
                        display_order: levels.length,
                        enabled: true,
                        questions_count: null,
                      });
                      categories[index] = { ...row, levels };
                      set({ categories });
                    }}
                  >
                    Add level
                  </Button>
                </Space>
              </Space>
            ))}
          </Space>
        </Card>
      )}

      <Button type="primary" loading={saving} onClick={() => save()}>
        Save Quiz Config
      </Button>
    </Space>
  );
};

export default ConfigTab;
